import { isFullAddress } from "@Light/utils/address";
import { getUtilityBehaviorFromRequirements } from "@Light/utils/utility";
import { ConfirmRate } from "./ConfirmRate";
import { CreditCheck } from "./CreditCheck";
import { PaymentSetup } from "./PaymentSetup";
import { ConfirmAddress } from "./PickAddress/ConfirmAddress";
import { ManualServiceAddress } from "./PickAddress/ManualServiceAddress";
import { ManualUtilityAccountNumber } from "./PickAddress/ManualUtilityAccountNumber";
import { PickAddressIntro } from "./PickAddress/PickAddressIntro";
import { PlanSelection } from "./PlanSelection";
import { EnrollRoute } from "./routes";
import { VerifyAndEnroll } from "./VerifyAndEnroll";
import { VerifyIdentity } from "./VerifyIdentity/VerifyIdentity";
import { ZipUtility } from "./ZipUtility/ZipUtility";
import { ZipUtilityIneligible } from "./ZipUtility/ZipUtilityIneligible";
import { ZipUtilitySearch } from "./ZipUtility/ZipUtilitySearch";
import { ZipUtilitySplit } from "./ZipUtility/ZipUtilitySplit";
import { always, existing, required, when } from "./flow/matchers";
import type { EnrollmentScreen, Requirements } from "./flow/types";

const getIsManualLocationEntry = (req: Requirements) =>
  getUtilityBehaviorFromRequirements(req).serviceLocationEntry === "manual";
const getIsSearchLocationEntry = (req: Requirements) =>
  getUtilityBehaviorFromRequirements(req).serviceLocationEntry === "search";

const selectPlanScreen: EnrollmentScreen = {
  path: EnrollRoute.PlanSelection,
  matches: [required("plan"), existing("plan")],
  component: PlanSelection,
};

const rateScreen: EnrollmentScreen = {
  path: EnrollRoute.ConfirmRate,
  matches: [always()],
  component: ConfirmRate,
  expanded: true,
};

const identityScreen: EnrollmentScreen = {
  path: EnrollRoute.VerifyIdentity,
  matches: [
    required("name"),
    required("email"),
    required("service_event"),
    required("service_start_date"),
  ],
  component: VerifyIdentity,
};

const creditCheckScreen: EnrollmentScreen = {
  path: EnrollRoute.CreditCheck,
  matches: [required("phone_number"), required("dob"), required("ssn")],
  component: CreditCheck,
};

const paymentSetupScreen: EnrollmentScreen = {
  path: EnrollRoute.PaymentSetup,
  matches: [required("payment_method")],
  component: PaymentSetup,
};

const confirmAndEnrollScreen: EnrollmentScreen = {
  path: EnrollRoute.VerifyAndEnroll,
  matches: [always()],
  component: VerifyAndEnroll,
};

const addressConfirmScreen: EnrollmentScreen = {
  path: EnrollRoute.ConfirmAddress,
  matches: [
    existing("utility_account_number"),
    when((requirements) => isFullAddress(requirements.service_address?.value)),
  ],
  component: ConfirmAddress,
};

const addressSearchScreen: EnrollmentScreen = {
  path: EnrollRoute.PickAddressIntro,
  matches: [required("service_address"), required("utility_account_number")],
  component: PickAddressIntro,
};

export const zipFirstEnrollmentScreens: EnrollmentScreen[] = [
  selectPlanScreen,
  {
    // Routes to one of the zip utility screens below. Can't be expressed
    // as a matcher because it relies on ZIP eligibility API request.
    path: EnrollRoute.ZipUtility,
    matches: [required("utility"), required("broker_terms_agreement")],
    component: ZipUtility,
  },
  {
    path: EnrollRoute.ZipUtilitySearch,
    matches: [],
    component: ZipUtilitySearch,
  },
  {
    path: EnrollRoute.ZipUtilitySplit,
    matches: [],
    component: ZipUtilitySplit,
  },
  {
    path: EnrollRoute.ZipUtilityIneligible,
    matches: [],
    component: ZipUtilityIneligible,
  },
  rateScreen,
  {
    ...addressConfirmScreen,
    matches: [...addressConfirmScreen.matches, when(getIsSearchLocationEntry)],
  },
  {
    ...addressSearchScreen,
    matches: [...addressSearchScreen.matches, when(getIsSearchLocationEntry)],
  },
  {
    path: EnrollRoute.ManualServiceAddress,
    matches: [
      required("service_address"),
      existing("service_address"),
      when(getIsManualLocationEntry),
    ],
    component: ManualServiceAddress,
  },
  {
    path: EnrollRoute.ManualUtilityAccountNumber,
    matches: [
      required("utility_account_number"),
      existing("utility_account_number"),
      when(getIsManualLocationEntry),
    ],
    component: ManualUtilityAccountNumber,
  },
  identityScreen,
  creditCheckScreen,
  paymentSetupScreen,
  confirmAndEnrollScreen,
];

export const addressFirstEnrollmentScreens: EnrollmentScreen[] = [
  selectPlanScreen,
  addressConfirmScreen,
  addressSearchScreen,
  rateScreen,
  identityScreen,
  creditCheckScreen,
  paymentSetupScreen,
  confirmAndEnrollScreen,
];
